import { useState, useMemo, useEffect, useCallback } from 'react';
import { useRestaurant } from '@/lib/session';
import {
  fetchStaff,
  upsertStaff,
  setStaffPin,
  pinInUse,
  deleteStaff,
  fetchAudit,
  type StaffMember,
  type AuditEntry,
} from '@/lib/data';

const roles = ['All Roles', 'Owner', 'Manager', 'Chef', 'Sous Chef', 'Captain', 'Waiter', 'Cashier', 'Cleaner'] as const;
const permissionOptions = ['orders', 'menu', 'inventory', 'customers', 'reports', 'staff', 'settings'];

type StaffFormData = {
  name: string;
  role: string;
  phone: string;
  email: string;
  pin: string;
  pinConfirm: string;
  shift: string;
  status: string;
  permissions: string[];
};

const emptyStaffForm: StaffFormData = {
  name: '', role: 'Waiter', phone: '', email: '', pin: '', pinConfirm: '', shift: 'Morning', status: 'Active', permissions: [],
};

const avatarPalette = ['bg-primary-500', 'bg-accent-500', 'bg-secondary-500', 'bg-green-500', 'bg-amber-500', 'bg-teal-500', 'bg-indigo-500', 'bg-purple-500'];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
}

export default function StaffPage() {
  const restaurant = useRestaurant();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('All Roles');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [activeTab, setActiveTab] = useState<'staff' | 'activity'>('staff');
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  // Activity log (persisted POS audit trail)
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');

  // Add / Edit Staff modal
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [staffForm, setStaffForm] = useState<StaffFormData>({ ...emptyStaffForm });

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<StaffMember | null>(null);

  // Toast
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setStaff(await fetchStaff(restaurant.id));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load staff.');
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditError('');
    try {
      setAudit(await fetchAudit(restaurant.id));
    } catch (e) {
      setAuditError(e instanceof Error ? e.message : 'Could not load activity.');
    } finally {
      setAuditLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { if (activeTab === 'activity') loadAudit(); }, [activeTab, loadAudit]);

  const showActionError = (e: unknown) => {
    setActionError(e instanceof Error ? e.message : 'Something went wrong.');
    setTimeout(() => setActionError(''), 4000);
  };

  const filteredStaff = useMemo(() => {
    let result = [...staff];

    if (roleFilter !== 'All Roles') {
      result = result.filter((s) => s.role === roleFilter);
    }
    if (statusFilter !== 'All') {
      result = result.filter((s) => s.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.role.toLowerCase().includes(q) ||
          (s.phone ?? '').includes(q)
      );
    }

    return result;
  }, [staff, search, roleFilter, statusFilter]);

  const statusFilters = ['All', 'Active', 'On Leave', 'Off Duty'];

  const activeNow = staff.filter((s) => s.status === 'Active').length;

  const openAddStaffModal = () => {
    setEditingStaff(null);
    setStaffForm({ ...emptyStaffForm });
    setStaffModalOpen(true);
  };

  const openEditStaffModal = (member: StaffMember) => {
    setEditingStaff(member);
    setStaffForm({
      name: member.name,
      role: member.role,
      phone: member.phone ?? '',
      email: member.email ?? '',
      pin: member.pin ?? '',
      pinConfirm: member.pin ?? '',
      shift: member.shift,
      status: member.status,
      permissions: [...member.permissions],
    });
    setStaffModalOpen(true);
  };

  const togglePermission = (perm: string) => {
    setStaffForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleStaffSave = async () => {
    if (!staffForm.name.trim() || !staffForm.phone.trim() || saving) return;

    // PIN validation: required on add, optional on edit (blank = keep existing).
    const pin = staffForm.pin.trim();
    if (!editingStaff && !pin) { showActionError(new Error('Set a 4-digit PIN so this person can sign in to the POS.')); return; }
    if (pin) {
      if (!/^\d{4}$/.test(pin)) { showActionError(new Error('PIN must be exactly 4 digits.')); return; }
      if (pin !== staffForm.pinConfirm.trim()) { showActionError(new Error('PINs do not match.')); return; }
    }

    setSaving(true);
    try {
      // Server-enforced uniqueness. Pre-check here so we never create a PIN-less
      // staff row when the chosen PIN collides. (PINs are hashed, so this can't
      // be checked client-side from the loaded list.)
      if (pin && (await pinInUse(pin, editingStaff?.id))) {
        throw new Error('That PIN is already in use. Choose a different 4-digit PIN.');
      }
      const base = {
        name: staffForm.name.trim(),
        role: staffForm.role,
        phone: staffForm.phone.trim() || null,
        email: staffForm.email.trim() || null,
        pin: pin || null,
        shift: staffForm.shift,
        status: staffForm.status,
        permissions: staffForm.permissions,
      };
      if (editingStaff) {
        const saved = await upsertStaff(restaurant.id, { ...base, id: editingStaff.id });
        if (pin) await setStaffPin(saved.id, pin); // also store a bcrypt hash (POS verifies via RPC)
        setStaff((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
        setSelectedStaff((prev) => (prev && prev.id === saved.id ? saved : prev));
        setToast(`${saved.name} updated`);
      } else {
        const saved = await upsertStaff(restaurant.id, {
          ...base,
          active: true,
          joinedDate: new Date().toISOString(),
        });
        if (pin) await setStaffPin(saved.id, pin); // also store a bcrypt hash
        setStaff((prev) => [...prev, saved]);
        setToast(`${saved.name} added to team`);
      }
      setStaffModalOpen(false);
      setEditingStaff(null);
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member: StaffMember) => {
    try {
      await deleteStaff(restaurant.id, member.id);
      setStaff((prev) => prev.filter((s) => s.id !== member.id));
      setDeleteTarget(null);
      setSelectedStaff(null);
      setToast(`${member.name} removed from team`);
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      showActionError(e);
    }
  };

  const handleToggleActive = async (member: StaffMember) => {
    try {
      const saved = await upsertStaff(restaurant.id, {
        id: member.id,
        name: member.name,
        role: member.role,
        active: !member.active,
      });
      setStaff((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
      setSelectedStaff((prev) => (prev && prev.id === saved.id ? saved : prev));
    } catch (e) {
      showActionError(e);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Staff &amp; Roles</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {staff.length} team members · {activeNow} active now
          </p>
        </div>
        <button
          onClick={openAddStaffModal}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line mr-1"></i> Add Staff
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-checkbox-circle-line text-green-500"></i>
          {toast}
        </div>
      )}

      {/* Error Toast */}
      {actionError && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-error-warning-line text-red-500"></i>
          {actionError}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total Staff</p>
          <p className="text-xl font-bold text-foreground-800">{staff.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Active Now</p>
          <p className="text-xl font-bold text-green-600">{activeNow}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">On Leave</p>
          <p className="text-xl font-bold text-amber-600">{staff.filter((s) => s.status === 'On Leave').length}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Off Duty</p>
          <p className="text-xl font-bold text-foreground-800">{staff.filter((s) => s.status === 'Off Duty').length}</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        <button
          onClick={() => setActiveTab('staff')}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === 'staff'
              ? 'bg-primary-500 text-background-50'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          Team Members
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === 'activity'
              ? 'bg-primary-500 text-background-50'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          Activity Log
        </button>
      </div>

      {loadError && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-500 mb-3">{loadError}</p>
          <button
            onClick={load}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !loadError && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
          <p className="text-sm text-foreground-500 mt-2">Loading staff...</p>
        </div>
      )}

      {!loading && !loadError && activeTab === 'staff' && (
        <>
          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                placeholder="Search staff..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-full"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {roles.map((role) => (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    roleFilter === role
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                  }`}
                >
                  {role}
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-1"></span>
              {statusFilters.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    statusFilter === s
                      ? 'bg-secondary-500 text-background-50 border-secondary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Staff Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredStaff.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedStaff(member)}
                className="bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-200 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-full ${avatarColor(member.name)} flex items-center justify-center shrink-0`}>
                    <span className="text-background-50 font-semibold text-sm">
                      {member.name.split(' ').map((n) => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-foreground-800 text-sm truncate">{member.name}</h4>
                      <StatusDot status={member.status} />
                    </div>
                    <p className="text-xs text-foreground-500">{member.role} · {member.shift} Shift</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1 text-foreground-500">
                    <i className="ri-phone-line text-foreground-400 w-4 h-4 flex items-center justify-center"></i>
                    <span>{member.phone ?? '—'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-foreground-500">
                    <span className="text-[10px] text-foreground-400">Last active:</span>
                    <span className="font-medium text-foreground-700">
                      {member.lastActive ? formatActivityTime(member.lastActive) : '—'}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  {member.permissions.slice(0, 3).map((perm) => (
                    <span key={perm} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {perm}
                    </span>
                  ))}
                  {member.permissions.length > 3 && (
                    <span className="text-[10px] text-foreground-400">+{member.permissions.length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredStaff.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-user-search-line text-xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-500">
                {staff.length === 0 ? 'No staff yet — add your first team member' : 'No staff match your filters'}
              </p>
            </div>
          )}
        </>
      )}

      {!loading && !loadError && activeTab === 'activity' && (
        <>
          {auditLoading && (
            <div className="py-16 text-center">
              <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
              <p className="text-sm text-foreground-500 mt-2">Loading activity...</p>
            </div>
          )}
          {auditError && !auditLoading && (
            <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
              <p className="text-sm text-foreground-500 mb-3">{auditError}</p>
              <button onClick={loadAudit} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">Retry</button>
            </div>
          )}
          {!auditLoading && !auditError && audit.length === 0 && (
            <div className="py-16 text-center bg-background-50 border border-background-200 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-history-line text-xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-500">No activity yet — staff actions (voids, discounts, transfers, settles) will appear here as they happen on the POS</p>
            </div>
          )}
          {!auditLoading && !auditError && audit.length > 0 && (
            <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-100">
              {audit.map((a) => (
                <div key={a.id} className="px-4 py-3 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${auditIsSensitive(a.type) ? 'bg-accent-50' : 'bg-secondary-100'}`}>
                    <i className={`${auditIcon(a.type)} text-sm ${auditIsSensitive(a.type) ? 'text-accent-500' : 'text-secondary-600'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground-800">{auditLabel(a.type)}</p>
                      <span className="text-xs text-foreground-400 shrink-0">{formatActivityTime(a.at)}</span>
                    </div>
                    <p className="text-xs text-foreground-600 mt-0.5 truncate">{a.detail}</p>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-foreground-400">
                      {a.by && <span><i className="ri-user-line mr-0.5"></i>{a.by}</span>}
                      {a.reason && <span className="px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">{a.reason}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Staff Detail Drawer */}
      {selectedStaff && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedStaff(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 shadow-lg h-full overflow-y-auto animate-[slideInRight_0.2s_ease-out]">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">Staff Details</h3>
              <button
                onClick={() => setSelectedStaff(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Profile */}
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full ${avatarColor(selectedStaff.name)} flex items-center justify-center shrink-0`}>
                  <span className="text-background-50 font-bold text-lg">
                    {selectedStaff.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-800">{selectedStaff.name}</h4>
                  <p className="text-sm text-foreground-500">{selectedStaff.role}</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <StatusDot status={selectedStaff.status} />
                <span className="text-sm font-medium text-foreground-700">{selectedStaff.status}</span>
                <span className="text-sm text-foreground-400">· {selectedStaff.shift} Shift</span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Joined</p>
                  <p className="text-sm font-semibold text-foreground-800">{selectedStaff.joinedDate ? formatShortDate(selectedStaff.joinedDate) : '—'}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Last Active</p>
                  <p className="text-sm font-semibold text-foreground-800">{selectedStaff.lastActive ? formatActivityTime(selectedStaff.lastActive) : '—'}</p>
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between bg-background-100 rounded-lg p-3">
                <div>
                  <p className="text-xs font-semibold text-foreground-500 uppercase">Account Active</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{selectedStaff.active ? 'Can sign in and take orders' : 'Access is disabled'}</p>
                </div>
                <button
                  onClick={() => handleToggleActive(selectedStaff)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${selectedStaff.active ? 'bg-green-500' : 'bg-background-300'}`}
                  title={selectedStaff.active ? 'Deactivate' : 'Activate'}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 shadow transition-all ${selectedStaff.active ? 'left-[22px]' : 'left-0.5'}`}></span>
                </button>
              </div>

              {/* Contact */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-shield-keyhole-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">
                    {selectedStaff.pin || selectedStaff.pinHash
                      ? <>POS PIN set <span className="text-foreground-400">(••••)</span></>
                      : <span className="text-amber-600">No PIN — can't sign in to POS yet</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-phone-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">{selectedStaff.phone ?? '—'}</span>
                </div>
                {selectedStaff.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-mail-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-foreground-700">{selectedStaff.email}</span>
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div>
                <p className="text-xs font-semibold text-foreground-500 uppercase mb-2">Permissions</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {selectedStaff.permissions.length > 0 ? selectedStaff.permissions.map((perm) => (
                    <span key={perm} className="text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 capitalize">
                      {perm}
                    </span>
                  )) : (
                    <span className="text-xs text-foreground-400">No special permissions</span>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2 pt-2 border-t border-background-200">
                <button
                  onClick={() => openEditStaffModal(selectedStaff)}
                  className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line mr-1.5"></i> Edit Profile
                </button>
                <button
                  onClick={() => setDeleteTarget(selectedStaff)}
                  className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1.5"></i> Remove Staff
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      {staffModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setStaffModalOpen(false); setEditingStaff(null); }}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h3>
              <button onClick={() => { setStaffModalOpen(false); setEditingStaff(null); }} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Name *</label>
                <input type="text" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="Full name" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Role</label>
                <select value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                  {['Owner','Manager','Chef','Sous Chef','Captain','Waiter','Cashier','Cleaner'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Phone *</label>
                <input type="tel" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="+91 98765 43210" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Email</label>
                <input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="staff@email.com" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">POS PIN {editingStaff ? '' : '*'}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={staffForm.pin}
                    onChange={(e) => setStaffForm({ ...staffForm, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="4 digits"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 tracking-widest focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Confirm PIN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={staffForm.pinConfirm}
                    onChange={(e) => setStaffForm({ ...staffForm, pinConfirm: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="Re-enter PIN"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 tracking-widest focus:outline-none focus:border-primary-300"
                  />
                </div>
              </div>
              <p className="text-[11px] text-foreground-400 -mt-2">This 4-digit PIN is what {staffForm.name.trim() || 'this person'} enters to sign in on the POS &amp; Captain apps.{editingStaff ? ' Leave unchanged to keep the current PIN.' : ''}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Shift</label>
                  <select value={staffForm.shift} onChange={(e) => setStaffForm({ ...staffForm, shift: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    {['Morning','Afternoon','Evening','Night','Split','Full Day'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Status</label>
                  <select value={staffForm.status} onChange={(e) => setStaffForm({ ...staffForm, status: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    {['Active','On Leave','Off Duty'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Permissions</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {permissionOptions.map((perm) => (
                    <button
                      key={perm}
                      type="button"
                      onClick={() => togglePermission(perm)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap capitalize ${
                        staffForm.permissions.includes(perm)
                          ? 'bg-secondary-500 text-background-50 border-secondary-500'
                          : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                      }`}
                    >
                      {perm}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={handleStaffSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving...' : editingStaff ? 'Save Changes' : 'Add Staff'}
                </button>
                <button onClick={() => { setStaffModalOpen(false); setEditingStaff(null); }} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 p-5 animate-[slideInUp_0.15s_ease-out]">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-800 mb-1">Remove Staff?</h4>
            <p className="text-center text-sm text-foreground-500 mb-4">"{deleteTarget.name}" will be removed from the team.</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => handleRemove(deleteTarget)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const colors: Record<string, string> = {
    'Active': 'bg-green-500',
    'On Leave': 'bg-amber-500',
    'Off Duty': 'bg-foreground-300',
  };
  return <span className={`w-2 h-2 rounded-full shrink-0 ${colors[status] || 'bg-foreground-300'}`}></span>;
}

const AUDIT_LABELS: Record<string, string> = {
  voidItem: 'Item voided', cancelKot: 'KOT cancelled', discount: 'Discount applied',
  reprint: 'KOT reprinted', transfer: 'Table transferred', merge: 'Tables merged', settle: 'Bill settled',
};
const AUDIT_ICONS: Record<string, string> = {
  voidItem: 'ri-indeterminate-circle-line', cancelKot: 'ri-close-circle-line', discount: 'ri-percent-line',
  reprint: 'ri-printer-line', transfer: 'ri-arrow-left-right-line', merge: 'ri-git-merge-line', settle: 'ri-checkbox-circle-line',
};
const AUDIT_SENSITIVE = new Set(['voidItem', 'cancelKot', 'discount']);
function auditLabel(t: string): string { return AUDIT_LABELS[t] ?? t; }
function auditIcon(t: string): string { return AUDIT_ICONS[t] ?? 'ri-history-line'; }
function auditIsSensitive(t: string): boolean { return AUDIT_SENSITIVE.has(t); }

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatActivityTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
